import { useNewToast } from 'hooks/newToast';
import { useSafra } from 'hooks/providers/configuracoes/safras';
import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from 'react';
import api from 'services';
import { toDate } from 'utils/converters';
import { appWarnings } from 'utils/appWarnings';
import { codigoCompletoEtiquetaLote } from 'pages/Rastreabilidade/utils/colheitaParametroDisplay';
import { lotesExigemHoraSaidaOrigem } from 'pages/Rastreabilidade/utils/armazemHoraSaidaOrigem';

const ArmazemContext = createContext({});

export function ArmazemProvider({ children }) {
  const { showToast } = useNewToast();
  const { safraAtual } = useSafra();

  const [lotesArmazem, setLotesArmazem] = useState([]);
  const [lotesArmazemLoading, setLotesArmazemLoading] = useState(false);
  const [loteArmazem, setLoteArmazem] = useState({});
  const [loteArmazemLoading, setLoteArmazemLoading] = useState(false);
  const [armazemLotesHistorico, setArmazemLotesHistorico] = useState([]);
  const [armazemLotesHistoricoTotalItems, setArmazemLotesHistoricoTotalItems] =
    useState(0);
  const [armazemLotesHistoricoLoading, setArmazemLotesHistoricoLoading] =
    useState(false);
  const [gerarNoArmazemLoading, setGerarNoArmazemLoading] = useState(false);
  const [editNoArmazemLoading, setEditNoArmazemLoading] = useState(false);
  const [deleteArmazemLoading, setDeleteArmazemLoading] = useState(false);

  const getLotesArmazem = useCallback(async (filter) => {
    try {
      setLotesArmazemLoading(true);
      const response = await api.armazem().getLotesArmazem(filter);
      setLotesArmazem(response);
      setLotesArmazemLoading(false);
    } catch (errors) {
      setLotesArmazemLoading(false);
    }
  }, []);

  const getLoteArmazem = useCallback(async (id) => {
    try {
      setLoteArmazemLoading(true);
      const response = await api.armazem().getLoteArmazem(id);
      setLoteArmazem(response);
      setLoteArmazemLoading(false);
    } catch (errors) {
      setLoteArmazemLoading(false);
    }
  }, []);

  const getLotesArmazemHistorico = useCallback(async (id) => {
    try {
      setArmazemLotesHistoricoLoading(true);
      const response = await api.armazem().getLotesArmazemHistorico(id);
      setArmazemLotesHistorico(response.content);
      setArmazemLotesHistoricoTotalItems(response?.pagination?.total || 0);
      setArmazemLotesHistoricoLoading(false);
    } catch (errors) {
      setArmazemLotesHistoricoLoading(false);
    }
  }, []);

  const gerarNoArmazem = useCallback(
    async (dados) => {
      try {
        setGerarNoArmazemLoading(true);
        const {
          pilha,
          data_entrada,
          hora_entrada,
          hora_saida_secagem,
          data_saida,
          responsavel,
          umidade_entrada,
          umidade_saida,
        } = dados.dados;
        const exigeHoraSaidaOrigem = lotesExigemHoraSaidaOrigem(
          dados.lotesArmazem,
        );
        const newData = {
          pilha: pilha.value,
          lote: dados.lotesArmazem.map((item) => {
            const lote = item?.lote || {};
            const codigo = codigoCompletoEtiquetaLote(lote);
            const qualidadeRaw =
              lote.qualidade ??
              lote.nome_qualidade ??
              lote.nomeQualidade ??
              lote.descricao_qualidade ??
              lote.descricaoQualidade ??
              lote.classificacao ??
              lote.classificacao_qualidade ??
              lote.classificacaoQualidade ??
              '';
            let qualidade = '';
            if (typeof qualidadeRaw === 'string') {
              qualidade = qualidadeRaw.trim();
            } else if (qualidadeRaw != null) {
              qualidade = String(qualidadeRaw).trim();
            }

            const payload = {
              id: lote.id,
              ref_id: lote.ref_id ?? lote.refId ?? lote.refID,
              ref_tipo: lote.ref_tipo ?? lote.refTipo,
              etapa_tipo: lote.etapa_tipo ?? lote.etapaTipo,
              codigo: codigo || undefined,
              quantidade: lote.quantidade ?? lote.qtd ?? lote.quantidade_total,
              medidas: lote.medidas ?? lote.valor_medida ?? lote.valorMedida,
            };

            if (qualidade) {
              payload.qualidade = qualidade;
            }

            // Campo auxiliar opcional (não obrigatório para o backend).
            if (lote.processo_id != null) {
              payload.processo_id = lote.processo_id;
            }

            // Remove campos vazios para manter compatibilidade com backend antigo
            Object.keys(payload).forEach((k) => {
              if (payload[k] === undefined) delete payload[k];
              if (payload[k] === '') delete payload[k];
              if (payload[k] === null) delete payload[k];
            });

            return payload;
          }),

          dataEntrada: toDate(data_entrada),
          horaEntrada: hora_entrada || undefined,
          horaSaidaSecagem: exigeHoraSaidaOrigem
            ? hora_saida_secagem || undefined
            : undefined,
          dataSaida:
            data_saida && data_saida !== 'Data inválida'
              ? toDate(data_saida)
              : null,
          umidadeEntrada: umidade_entrada,
          umidadeSaida: umidade_saida,
          responsavel,
          safra: dados?.safra ?? safraAtual?.id,
        };

        // DEBUG temporário: validar payload enviado ao backend
        // Remover quando o contrato estiver estabilizado.
        try {
          /* eslint-disable no-console */
          console.groupCollapsed('[Rastreabilidade] Enviar para armazém');
          console.groupEnd();
          /* eslint-enable no-console */
        } catch {}

        const response = await api.armazem().gerarNoArmazem(newData);
        setGerarNoArmazemLoading(false);
        showToast({
          type: 'success',
          message: 'Entrada no armazém gerada com sucesso!',
        });
        return response;
      } catch (err) {
        setGerarNoArmazemLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, safraAtual],
  );

  const editNoArmazem = useCallback(
    async (id, dados) => {
      try {
        setEditNoArmazemLoading(true);
        const {
          data_entrada,
          data_saida,
          responsavel,
          umidade_entrada,
          umidade_saida,
          pilha,
        } = dados.dados;

        const newData = {
          pilha: pilha.value,
          dataEntrada: toDate(data_entrada),
          dataSaida:
            data_saida && data_saida !== 'Data inválida'
              ? toDate(data_saida)
              : null,
          umidadeEntrada: umidade_entrada,
          umidadeSaida: umidade_saida === '' ? null : umidade_saida,
          responsavel,
        };
        const response = await api.armazem().editLoteArmazem(id, newData);
        setEditNoArmazemLoading(false);
        showToast({
          type: 'success',
          message: 'Entrada no armazém editada com sucesso!',
        });
        return response;
      } catch (err) {
        setEditNoArmazemLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const deleteArmazem = useCallback(
    async (codigo) => {
      try {
        setDeleteArmazemLoading(true);
        await api.armazem().deleteLoteArmazem(codigo);
        setLotesArmazem(lotesArmazem.filter((item) => item.id !== codigo));
        setDeleteArmazemLoading(false);
        getLotesArmazem();
        showToast({
          type: 'success',
          message: 'Processo de armazém removido com sucesso!',
        });
      } catch (err) {
        setDeleteArmazemLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [lotesArmazem, showToast, getLotesArmazem],
  );

  const contextValue = useMemo(
    () => ({
      gerarNoArmazemLoading,
      gerarNoArmazem,
      editNoArmazemLoading,
      editNoArmazem,
      deleteArmazemLoading,
      deleteArmazem,
      lotesArmazemLoading,
      lotesArmazem,
      loteArmazem,
      loteArmazemLoading,
      getLoteArmazem,
      getLotesArmazem,
      armazemLotesHistorico,
      armazemLotesHistoricoLoading,
      getLotesArmazemHistorico,
      armazemLotesHistoricoTotalItems,
    }),
    [
      gerarNoArmazemLoading,
      gerarNoArmazem,
      editNoArmazemLoading,
      editNoArmazem,
      deleteArmazemLoading,
      deleteArmazem,
      lotesArmazemLoading,
      lotesArmazem,
      loteArmazem,
      loteArmazemLoading,
      getLoteArmazem,
      getLotesArmazem,
      armazemLotesHistorico,
      armazemLotesHistoricoLoading,
      getLotesArmazemHistorico,
      armazemLotesHistoricoTotalItems,
    ],
  );

  return (
    <ArmazemContext.Provider value={contextValue}>
      {children}
    </ArmazemContext.Provider>
  );
}

export function useArmazem() {
  const context = useContext(ArmazemContext);

  if (!context)
    throw new Error('useArmazem must be used within an AuthProvider');

  return context;
}
